import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { cancelWithdrawal } from '@/features/withdraws/api/cancel-withdrawal.api';

export interface CancelWithdrawDialogProps extends BaseDialogProps {
  withdrawalId: number;
}

export default function CancelWithdrawDialog({
  dialog,
  withdrawalId,
}: CancelWithdrawDialogProps) {
  const { closeCancelWithdrawDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: () => cancelWithdrawal(withdrawalId),
    onSuccess: () => {
      toast.success('Penarikan berhasil dibatalkan');
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
      closeCancelWithdrawDialog();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof AxiosError
          ? (error.response?.data?.message ?? error.message)
          : 'Gagal membatalkan penarikan';
      toast.error(message);
    },
  });

  const isPending = cancelMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isPending) {
          closeCancelWithdrawDialog();
        }
      }}
    >
      <AlertDialogContent showCloseButton={false}>
        <AlertDialogHeader>
          <AlertDialogTitle>Batalkan penarikan ini?</AlertDialogTitle>
          <AlertDialogDescription>
            Tindakan ini tidak dapat dibatalkan. Penarikan saldo mentor akan
            dibatalkan.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Tutup</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isPending}
            onClick={() => cancelMutation.mutate()}
          >
            {isPending ? 'Membatalkan...' : 'Batalkan'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
